const resolveHref = (): string | undefined => {
  // Common case: not in an iframe — our own location IS the host page.
  if (window === window.top) {
    return window.location.href;
  }

  // We're in an iframe. Try the cross-window read (works for same-origin).
  // `?.` guards the sandboxed-iframe case where window.top can be null;
  // try/catch catches the SecurityError thrown for cross-origin parents.
  try {
    const topHref = window.top?.location.href;
    if (topHref) return topHref;
  } catch {
    // Cross-origin — fall through.
  }

  // Cross-origin iframe: best-effort via referrer. Returning undefined
  // when the referrer is empty is intentional — falling back to
  // window.location.href would report the iframe's own URL, which is
  // actively misleading for host-page analytics.
  return document.referrer || undefined;
};

// Strip the query string entirely (may contain OAuth `?code=...`,
// magic-link `?token=...`, etc.). Keep path-like fragments so hash-routed
// SPAs report distinct pages, but strip any fragment that contains `=`
// (signature of `key=value` token pairs like implicit-flow `#access_token=...`
// or tracking like `#fbclid=...`). Also drop any secondary query inside the
// fragment (`#/route?ref=email` → `#/route`).
const sanitizeFragment = (hash: string): string => {
  if (!hash) return "";
  const beforeSecondaryQuery = hash.split("?")[0];
  if (beforeSecondaryQuery.includes("=")) return "";
  return beforeSecondaryQuery;
};

export const getCurrentSanitizedUrl = (): string | undefined => {
  const href = resolveHref();
  if (!href) return undefined;

  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}${sanitizeFragment(url.hash)}`;
  } catch {
    return undefined;
  }
};
